import type {
  OutletSummary,
  ReportDimension,
  ReportExportDataset,
  ReportExportView,
  ReportOrderView,
  ReportRow,
  SalesReportView,
} from "@kopi/contracts";
import { MAX_REPORT_DAYS, MAX_REPORT_ROWS, reportDays } from "@kopi/contracts/client";
import { addDays, businessDate } from "@kopi/domain";
import { Badge } from "@kopi/ui/components/badge";
import { Button } from "@kopi/ui/components/button";
import { Card, CardContent } from "@kopi/ui/components/card";
import { DataTable, type DataTableColumn, type DataTableQuery } from "@kopi/ui/components/data-table";
import { DatePicker } from "@kopi/ui/components/date-picker";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@kopi/ui/components/dropdown-menu";
import { Label } from "@kopi/ui/components/label";
import { OptionSelect } from "@kopi/ui/components/option-select";
import { ResponsiveDialog } from "@kopi/ui/components/responsive-dialog";
import { Skeleton } from "@kopi/ui/components/skeleton";
import { toast } from "@kopi/ui/components/sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@kopi/ui/components/tabs";
import { cn } from "@kopi/ui/lib/utils";
import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  CalendarIcon,
  CalendarRangeIcon,
  ChartColumnIcon,
  CircleCheckIcon,
  ClockIcon,
  CloudCheckIcon,
  CloudOffIcon,
  CreditCardIcon,
  DownloadIcon,
  HourglassIcon,
  InfoIcon,
  LayoutGridIcon,
  ReceiptTextIcon,
  RefreshCwIcon,
  ScaleIcon,
  ShoppingBagIcon,
  StoreIcon,
  TrendingUpIcon,
  TrophyIcon,
  Undo2Icon,
  UtensilsIcon,
  WalletIcon,
} from "lucide-react";
import { type ReactNode, useEffect, useId, useMemo, useRef, useState } from "react";
import { useCurrentOutlet } from "@/components/current-outlet";
import { Page, PageHeader } from "@/components/page-header";
import {
  ColumnChart,
  FigureCard,
  figureSpan,
  hourBuckets,
  MixBreakdown,
  PanelCard,
  RankedList,
  type Slice,
  type Tone,
} from "@/components/sales-visuals";
import { INTL_LOCALE } from "@/i18n/locale";
import { reports } from "@/i18n/messages/reports";
import { useMessages } from "@/i18n/provider";
import { canAnywhere, canAt } from "@/lib/access";
import { ApiRequestError, api, apiUrl, unwrap } from "@/lib/api";
import { CHANNEL_KEYS } from "@/lib/channels";
import { meScopesQuery, outletsQuery, PAGE } from "@/lib/queries";
import { calendarDay, isoDay, isoInstant } from "@/lib/report-dates";

const ALL = "all";
const KEY = ["reports", "sales"] as const;

const useReportMessages = () => useMessages(reports);
type ReportsT = ReturnType<typeof useReportMessages>["t"];
type Format = ReturnType<typeof useReportMessages>["format"];

type Preset = "today" | "yesterday" | "week" | "month" | "custom";
const PRESETS: Exclude<Preset, "custom">[] = ["today", "yesterday", "week", "month"];

interface Filters {
  outletId: string;
  from: string;
  to: string;
}

/** The breakdown tabs, in the order a manager reads them; Outlet only when several are in scope. */
const DIMENSIONS: ReportDimension[] = [
  "product",
  "category",
  "hour",
  "cashier",
  "channel",
  "orderType",
  "payment",
  "outlet",
];
const LINE_DIMENSIONS = new Set<ReportDimension>(["product", "category"]);

/** A preset's business dates, read on the outlet's own clock and cutoff. */
function presetRange(preset: Exclude<Preset, "custom">, outlet: OutletSummary | undefined) {
  const today = businessDate(
    new Date(),
    outlet?.timezone ?? "Asia/Jakarta",
    outlet?.businessDayCutoff ?? "04:00",
  );
  switch (preset) {
    case "today":
      return { from: today, to: today };
    case "yesterday":
      return { from: addDays(today, -1), to: addDays(today, -1) };
    case "week":
      return { from: addDays(today, -6), to: today };
    case "month":
      return { from: `${today.slice(0, 8)}01`, to: today };
  }
}

/** The API's own limit, checked here too, so the request is never one it refuses. */
const validRange = (f: Filters) =>
  Boolean(f.from && f.to) && f.from <= f.to && reportDays(f.from, f.to) <= MAX_REPORT_DAYS;

const reportQuery = (filters: Filters) =>
  queryOptions({
    queryKey: [...KEY, filters],
    queryFn: async () =>
      unwrap(
        await api.v1.reports.sales.get({
          query: {
            from: filters.from,
            to: filters.to,
            ...(filters.outletId === ALL ? {} : { outletId: filters.outletId }),
          },
        }),
      ),
    enabled: validRange(filters),
    // The previous figures stay on screen while a new range loads.
    placeholderData: keepPreviousData,
  });

interface Drill {
  dimension: ReportDimension | null;
  key: string | null;
  label: string;
}

/** The sales behind one figure, a page at a time (CODING_STANDARDS §4). */
const drillQuery = (filters: Filters, drill: Drill) =>
  infiniteQueryOptions({
    queryKey: [...KEY, "orders", filters, drill.dimension, drill.key],
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page: { nextCursor: string | null }) => page.nextCursor ?? undefined,
    queryFn: async ({ pageParam }) =>
      unwrap(
        await api.v1.reports.sales.orders.get({
          query: {
            from: filters.from,
            to: filters.to,
            limit: PAGE,
            ...(filters.outletId === ALL ? {} : { outletId: filters.outletId }),
            ...(drill.dimension && drill.key !== null ? { dimension: drill.dimension, key: drill.key } : {}),
            ...(pageParam ? { cursor: pageParam } : {}),
          },
        }),
      ),
  });

export const Route = createFileRoute("/_authed/reports")({
  staticData: { section: "workspace", crumb: "nav.reports" },
  loader: ({ context }) => context.queryClient.ensureQueryData(outletsQuery),
  component: ReportsPage,
});

/** A breakdown row's label in the interface language; tenant names (products, cashiers) as typed. */
function rowLabel(t: ReportsT, dimension: ReportDimension, row: ReportRow): string {
  switch (dimension) {
    case "hour": {
      const hour = Number(row.key);
      const pad = (n: number) => String(n % 24).padStart(2, "0");
      return t("reports.hour", { from: pad(hour), to: pad(hour + 1) });
    }
    case "orderType":
      return row.key in CHANNEL_KEYS ? t(CHANNEL_KEYS[row.key as keyof typeof CHANNEL_KEYS]) : row.label;
    case "channel":
      return t(`reports.channel.${row.key as "pos" | "grabfood" | "gofood" | "shopeefood"}`);
    case "payment":
      return t(`reports.payment.${row.key as "cash" | "qris" | "manual_qris_static" | "marketplace"}`);
    default:
      return row.label;
  }
}

const paymentLabel = (t: ReportsT, payment: ReportOrderView["payment"]) =>
  payment ? t(`reports.payment.${payment}`) : "—";

/**
 * A bounded breakdown (≤ MAX_REPORT_ROWS, CODING_STANDARDS §4 exception) as the table's pages: cut into
 * `PAGE`-row pages so the table pages through it with Prev/Next like the server-paged lists.
 */
function staticRows<T>(
  items: readonly T[],
  pending: boolean,
  error: boolean,
  refetch: () => unknown,
): DataTableQuery<T> {
  const pages = Array.from({ length: Math.max(1, Math.ceil(items.length / PAGE)) }, (_, i) => ({
    items: items.slice(i * PAGE, (i + 1) * PAGE),
    nextCursor: null,
  }));
  return {
    data: pending ? undefined : { pages },
    isPending: pending,
    isError: error,
    isFetchNextPageError: false,
    refetch,
    hasNextPage: false,
    isFetchingNextPage: false,
    fetchNextPage: () => undefined,
  };
}

function columnsFor(
  t: ReportsT,
  format: Format,
  dimension: ReportDimension,
  financial: boolean,
): DataTableColumn<ReportRow>[] {
  const money = (value: string | null) => (value === null ? "—" : format.money(BigInt(value)));
  const line = LINE_DIMENSIONS.has(dimension);
  const columns: DataTableColumn<ReportRow>[] = [
    {
      id: "label",
      header: t(`reports.dim.${dimension}`),
      meta: { card: "title" },
      cell: ({ row }) => <span className="font-medium">{rowLabel(t, dimension, row.original)}</span>,
    },
    {
      id: "orders",
      header: t("reports.col.orders"),
      meta: { align: "end" },
      cell: ({ row }) => row.original.orders,
    },
  ];
  if (line)
    columns.push({
      id: "quantity",
      header: t("reports.col.quantity"),
      meta: { align: "end" },
      cell: ({ row }) => row.original.quantity ?? "—",
    });
  columns.push(
    {
      id: "gross",
      header: t("reports.col.gross"),
      meta: { align: "end" },
      cell: ({ row }) => money(row.original.gross),
    },
    {
      id: "discount",
      header: t("reports.col.discount"),
      meta: { align: "end" },
      cell: ({ row }) => money(row.original.discount),
    },
    {
      id: "net",
      header: t("reports.col.net"),
      meta: { align: "end" },
      cell: ({ row }) => <span className="font-medium">{money(row.original.net)}</span>,
    },
  );
  if (!line)
    columns.push({
      id: "total",
      header: t("reports.col.total"),
      meta: { align: "end" },
      cell: ({ row }) => money(row.original.total),
    });
  if (financial)
    columns.push(
      {
        id: "cost",
        header: t("reports.col.cost"),
        meta: { align: "end" },
        cell: ({ row }) => money(row.original.estimatedCost),
      },
      {
        id: "margin",
        header: t("reports.col.margin"),
        meta: { align: "end" },
        cell: ({ row }) => money(row.original.estimatedMargin),
      },
    );
  return columns;
}

/**
 * The operational sales report (R1-43, PRD FIN-01): an outlet's (or every reportable outlet's) sales
 * over its business days, with the disclosures AC-31 asks for — timezone and cutoff, when the figures
 * were read and which offline sales they can't include yet — breakdowns that drill down to the sales
 * behind them, and formula-safe CSV exports. Cost and margin show only with `reports.financial`.
 */
function ReportsPage() {
  const { t, locale } = useReportMessages();
  const { data: scopes } = useSuspenseQuery(meScopesQuery);
  const { data: outlets } = useSuspenseQuery(outletsQuery);
  const { outlet: current } = useCurrentOutlet();
  const reportable = outlets.filter((o) => canAt(scopes, "reports.operational", o.id));
  const initialOutlet = reportable.find((o) => o.id === current?.id) ?? reportable[0];
  const [preset, setPreset] = useState<Preset>("today");
  const [filters, setFilters] = useState<Filters>(() => ({
    outletId: initialOutlet?.id ?? "",
    ...presetRange("today", initialOutlet),
  }));
  const [drill, setDrill] = useState<Drill | null>(null);
  const report = useQuery(reportQuery(filters));
  const ids = { outletLabel: useId(), from: useId(), fromLabel: useId(), to: useId(), toLabel: useId() };

  if (reportable.length === 0) {
    return (
      <Page>
        <PageHeader title={t("nav.reports")} subtitle={t("reports.noOutlet")} />
      </Page>
    );
  }

  // Presets follow the chosen outlet's clock; across outlets, the first one's.
  const clockOutlet = reportable.find((o) => o.id === filters.outletId) ?? reportable[0];
  const pickPreset = (next: Preset) => {
    setPreset(next);
    if (next !== "custom") setFilters((f) => ({ ...f, ...presetRange(next, clockOutlet) }));
  };
  const pickDate = (field: "from" | "to", value: string) => {
    setPreset("custom");
    setFilters((f) => ({ ...f, [field]: value }));
  };
  const data = report.data;
  const loading = report.isPending && validRange(filters);

  return (
    <Page>
      <PageHeader
        title={t("nav.reports")}
        subtitle={t("reports.subtitle")}
        actions={<ExportMenu filters={filters} disabled={!data || !validRange(filters)} />}
      />

      <Card className="gap-0 py-0">
        <div className="flex flex-col gap-4 p-4 sm:p-5">
          {/* Quick ranges as one segmented strip; it scrolls on a phone rather than wrapping. */}
          <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
            <fieldset className="inline-flex min-w-max gap-1 rounded-lg bg-muted p-1">
              <legend className="sr-only">{t("reports.preset")}</legend>
              {[...PRESETS, "custom" as const].map((p) => (
                <Button
                  key={p}
                  type="button"
                  variant="ghost"
                  aria-pressed={preset === p}
                  onClick={() => pickPreset(p)}
                  className={cn(
                    "h-10 rounded-md px-3.5 text-muted-foreground hover:bg-card/60 hover:text-foreground",
                    preset === p && "bg-card text-foreground shadow-sm hover:bg-card",
                  )}
                >
                  {p === "custom" && <CalendarRangeIcon />}
                  {t(`reports.preset.${p}`)}
                </Button>
              ))}
            </fieldset>
          </div>
          <div
            className={cn(
              "grid items-end gap-3 sm:grid-cols-2",
              reportable.length > 1
                ? "lg:grid-cols-[minmax(0,1.2fr)_1fr_1fr_auto]"
                : "lg:grid-cols-[1fr_1fr_auto]",
            )}
          >
            {reportable.length > 1 && (
              <div className="flex min-w-0 flex-col gap-1.5 sm:col-span-2 lg:col-span-1">
                <Label
                  id={ids.outletLabel}
                  className="flex items-center gap-1.5 text-muted-foreground text-xs"
                >
                  <StoreIcon aria-hidden="true" className="size-3.5" />
                  {t("reports.outlet")}
                </Label>
                {/* A tenant's outlets are a small, bounded set (CODING_STANDARDS §5: OptionSelect is fine). */}
                <OptionSelect
                  aria-labelledby={ids.outletLabel}
                  options={[
                    { value: ALL, label: t("reports.allOutlets") },
                    ...reportable.map((o) => ({ value: o.id, label: o.name })),
                  ]}
                  value={filters.outletId}
                  onValueChange={(outletId) => setFilters((f) => ({ ...f, outletId }))}
                />
              </div>
            )}
            <div className="flex min-w-0 flex-col gap-1.5">
              <Label
                id={ids.fromLabel}
                htmlFor={ids.from}
                className="flex items-center gap-1.5 text-muted-foreground text-xs"
              >
                <CalendarIcon aria-hidden="true" className="size-3.5" />
                {t("reports.from")}
              </Label>
              <DatePicker
                locale={locale}
                id={ids.from}
                labelId={ids.fromLabel}
                value={filters.from}
                onChange={(value) => pickDate("from", value)}
                placeholder={t("reports.from")}
                className="min-w-0"
              />
            </div>
            <div className="flex min-w-0 flex-col gap-1.5">
              <Label
                id={ids.toLabel}
                htmlFor={ids.to}
                className="flex items-center gap-1.5 text-muted-foreground text-xs"
              >
                <CalendarIcon aria-hidden="true" className="size-3.5" />
                {t("reports.to")}
              </Label>
              <DatePicker
                locale={locale}
                id={ids.to}
                labelId={ids.toLabel}
                value={filters.to}
                onChange={(value) => pickDate("to", value)}
                placeholder={t("reports.to")}
                className="min-w-0"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              className="sm:col-span-2 lg:col-span-1"
              onClick={() => void report.refetch()}
              disabled={!validRange(filters) || report.isFetching}
            >
              <RefreshCwIcon className={report.isFetching ? "motion-safe:animate-spin" : undefined} />
              {t("reports.refresh")}
            </Button>
          </div>
          {!validRange(filters) && (
            <p role="alert" className="text-destructive text-sm">
              {t("reports.rangeInvalid")}
            </p>
          )}
        </div>
        {validRange(filters) && <ScopeLine report={data} />}
      </Card>

      {!validRange(filters) ? null : report.isError && !data ? (
        <Card>
          <CardContent className="flex flex-col items-start gap-3">
            <p role="alert">{t("reports.loadFailed")}</p>
            <Button type="button" variant="outline" onClick={() => void report.refetch()}>
              {t("reports.retry")}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {data && <Backlog report={data} canReview={canAnywhere(scopes, "offline.reconcile")} />}
          <Kpis report={data} loading={loading} />
          <Highlights report={loading ? undefined : data} />
          <Breakdowns
            report={data}
            loading={loading}
            error={report.isError}
            refetch={() => void report.refetch()}
            onDrill={setDrill}
          />
          <div className="grid items-start gap-4 lg:grid-cols-2">
            <Summary
              report={data}
              onDrill={() => setDrill({ dimension: null, key: null, label: t("reports.drill.all") })}
            />
            <InclusionRules financial={data?.financial ?? false} />
          </div>
        </>
      )}
      {drill && (
        <DrillDialog
          filters={filters}
          drill={drill}
          zones={new Map(data?.outlets.map((o) => [o.id, o.timezone]))}
          onClose={() => setDrill(null)}
        />
      )}
    </Page>
  );
}

/** A section's heading, with an optional caption on the right. */
function SectionHeading(props: { id: string; title: string; caption?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
      <h2 id={props.id} className="font-semibold text-lg tracking-tight">
        {props.title}
      </h2>
      {props.caption && <p className="text-muted-foreground text-sm">{props.caption}</p>}
    </div>
  );
}

/** What the figures cover and when they were read (AC-31), as the filter card's footer. */
function ScopeLine(props: { report: SalesReportView | undefined }) {
  const { t, format } = useReportMessages();
  const { report } = props;
  if (!report)
    return (
      <div className="flex gap-3 border-t px-4 py-3 sm:px-5" aria-busy="true">
        <Skeleton className="h-4 w-2/3" />
      </div>
    );
  const day = (date: string) => format.date(calendarDay(date));
  const zones = new Set(report.outlets.map((o) => `${o.timezone} ${o.businessDayCutoff}`));
  const first = report.outlets[0];
  const offline = report.backlog.offlineRegisters.length === 0 && report.backlog.quarantined === 0;
  return (
    <ul className="flex flex-wrap gap-x-5 gap-y-2 border-t bg-muted/40 px-4 py-3 text-muted-foreground text-sm sm:px-5">
      <li className="flex items-start gap-2">
        <CalendarRangeIcon aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
        <span>
          <span className="font-medium text-foreground">
            {isoDay(report.from) === isoDay(report.to)
              ? t("reports.scope.day", { from: day(report.from) })
              : t("reports.scope.days", { from: day(report.from), to: day(report.to) })}
          </span>
          {" · "}
          {zones.size === 1 && first
            ? t("reports.scope.zone", { zone: first.timezone, cutoff: first.businessDayCutoff })
            : t("reports.scope.zones")}
        </span>
      </li>
      <li className="flex items-start gap-2">
        <ClockIcon aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
        <span>
          {t("reports.scope.fresh", { time: format.dateTime(report.generatedAt, first?.timezone) })}
          {" · "}
          {report.latestSaleAt
            ? t("reports.scope.latest", { time: format.dateTime(report.latestSaleAt, first?.timezone) })
            : t("reports.scope.noSales")}
        </span>
      </li>
      {offline && (
        <li className="flex items-start gap-2">
          <CloudCheckIcon aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-success" />
          <span>{t("reports.backlog.clear")}</span>
        </li>
      )}
    </ul>
  );
}

/**
 * What the figures may not include yet (AC-31): registers still holding offline sales, and offline
 * sales held for review. Shown only when there is any, as a warning the owner can act on.
 */
function Backlog(props: { report: SalesReportView; canReview: boolean }) {
  const { t, format } = useReportMessages();
  const { report } = props;
  const registers = report.backlog.offlineRegisters;
  const quarantined = report.backlog.quarantined;
  if (registers.length === 0 && quarantined === 0) return null;
  const zone = report.outlets[0]?.timezone;
  return (
    <section
      aria-label={t("reports.backlog.title")}
      className="flex gap-3 rounded-xl border border-warning/40 bg-warning/10 p-4 text-sm sm:p-5"
    >
      <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-warning/15 text-warning [&_svg]:size-[18px]">
        <CloudOffIcon aria-hidden="true" />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <h2 className="font-semibold text-base">{t("reports.backlog.title")}</h2>
        {registers.length > 0 && (
          <div className="flex flex-col gap-1">
            <span>{t("reports.backlog.registers", { count: registers.length })}</span>
            <ul className="flex flex-col gap-0.5 text-muted-foreground">
              {registers.map((r) => (
                <li key={r.registerId}>
                  {t("reports.backlog.register", {
                    register: r.registerName,
                    outlet: r.outletName,
                    synced: r.lastSyncedAt
                      ? format.dateTime(r.lastSyncedAt, zone)
                      : t("reports.backlog.neverSynced"),
                  })}
                </li>
              ))}
            </ul>
          </div>
        )}
        {quarantined > 0 && (
          <div className="flex flex-wrap items-center gap-3">
            <Badge variant="warning">{quarantined}</Badge>
            <span className="min-w-0 flex-1">{t("reports.backlog.quarantined", { count: quarantined })}</span>
            {props.canReview && (
              <Button asChild variant="outline" size="sm">
                <Link to="/offline-exceptions" search={{ status: "open" }}>
                  {t("reports.backlog.review")}
                </Link>
              </Button>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

interface Figure {
  icon: ReactNode;
  tone: Tone;
  label: string;
  value: string;
  note: string;
}

/**
 * The headline figures: net sales leads in Forest, the rest on cards with a tinted icon. Five figures
 * give net sales two columns beside orders on a wide screen; six sit three by two.
 */
function Kpis(props: { report: SalesReportView | undefined; loading: boolean }) {
  const { t, format } = useReportMessages();
  const s = props.report?.summary;
  const money = (value: string | undefined) => format.money(BigInt(value ?? "0"));
  const sum = (a: string | undefined, b: string | undefined) =>
    format.money(BigInt(a ?? "0") + BigInt(b ?? "0"));
  const figures: Figure[] = [
    {
      icon: <WalletIcon />,
      tone: "primary",
      label: t("reports.kpi.net"),
      value: money(s?.net),
      note: t("reports.kpi.netNote", { gross: money(s?.gross), discounts: money(s?.discounts) }),
    },
    {
      icon: <ShoppingBagIcon />,
      tone: "secondary",
      label: t("reports.kpi.orders"),
      value: t("reports.kpi.ordersValue", { count: s?.orders ?? 0 }),
      note: t("reports.kpi.ordersNote", { average: money(s?.averageTicket) }),
    },
    {
      icon: <Undo2Icon />,
      tone: "destructive",
      label: t("reports.kpi.refunds"),
      value: money(s?.refunds.amount),
      note: t("reports.kpi.refundsNote", {
        count: s?.refunds.count ?? 0,
        pending: money(s?.pendingRefunds.amount),
      }),
    },
    {
      icon: <ScaleIcon />,
      tone: "copper",
      label: t("reports.kpi.variance"),
      value: money(s?.cash.variance),
      note: t("reports.kpi.varianceNote", {
        closed: s?.cash.shiftsClosed ?? 0,
        open: s?.cash.shiftsUnclosed ?? 0,
      }),
    },
    {
      icon: <HourglassIcon />,
      tone: "warning",
      label: t("reports.kpi.unsettled"),
      value: sum(s?.awaitingPayment.amount, s?.marketplaceReceivable.amount),
      note: t("reports.kpi.unsettledNote", {
        awaiting: money(s?.awaitingPayment.amount),
        receivable: money(s?.marketplaceReceivable.amount),
      }),
    },
  ];
  if (s?.estimatedMargin)
    figures.push({
      icon: <TrendingUpIcon />,
      tone: "success",
      label: t("reports.kpi.margin"),
      // No costed sale: the estimate has nothing to stand on, so it isn't shown as a zero.
      value: s.estimatedMargin.costedOrders === 0 ? "—" : money(s.estimatedMargin.margin),
      note: t("reports.kpi.marginNote", { uncosted: s.estimatedMargin.uncostedOrders }),
    });
  const count = figures.length;
  return (
    <section aria-labelledby="reports-figures" className="flex flex-col gap-3">
      <h2 id="reports-figures" className="sr-only">
        {t("reports.summary")}
      </h2>
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3">
        {figures.map((figure, index) => (
          <FigureCard
            key={figure.label}
            icon={figure.icon}
            tone={figure.tone}
            label={figure.label}
            value={figure.value}
            featured={index === 0}
            loading={props.loading}
            footer={<span>{figure.note}</span>}
            className={figureSpan(
              index,
              count,
              index === 0 && count === 5 ? "lg:col-span-2" : "lg:col-span-1",
            )}
          />
        ))}
      </div>
    </section>
  );
}

/** How many categories the mix names; the rest are summed as one "Other" slice. */
const MIX_SLICES = 4;
const TOP_PRODUCTS = 5;

/**
 * The report at a glance: net sales by hour (summed over the range), the payment mix, best sellers,
 * the category mix and how orders were taken. Every figure is also in the breakdown tables below.
 */
function Highlights(props: { report: SalesReportView | undefined }) {
  const { t, format, locale } = useReportMessages();
  const report = props.report;
  const rows = (dimension: ReportDimension) => report?.breakdowns[dimension];
  const intl = INTL_LOCALE[locale];
  const hh = (hour: number) => t("reports.hourTick", { hour: String(hour % 24).padStart(2, "0") });
  const hours = rows("hour");
  const slices = (dimension: ReportDimension, amount: "net" | "total") =>
    rows(dimension)?.map((row) => ({
      key: row.key,
      label: rowLabel(t, dimension, row),
      orders: row.orders,
      amount: BigInt((amount === "total" ? row.total : null) ?? row.net),
    }));
  const categories = slices("category", "net");
  const categoryMix = categories && [
    ...categories.slice(0, MIX_SLICES),
    ...(categories.length > MIX_SLICES
      ? [
          {
            key: "other",
            label: t("reports.other"),
            orders: categories.slice(MIX_SLICES).reduce((n, c) => n + c.orders, 0),
            amount: categories.slice(MIX_SLICES).reduce((n, c) => n + c.amount, 0n),
          },
        ]
      : []),
  ];
  const detail = (slice: Slice) =>
    t("reports.slice", { count: slice.orders, amount: format.money(slice.amount) });
  const empty = t("reports.emptyHint");

  return (
    <section aria-labelledby="reports-highlights" className="flex flex-col gap-3">
      <SectionHeading id="reports-highlights" title={t("reports.highlights")} />
      <div className="grid items-start gap-4 xl:grid-cols-3">
        <PanelCard
          icon={<ChartColumnIcon />}
          title={t("reports.chart.hour")}
          caption={t("reports.chart.hourCaption")}
          className="h-auto min-w-0 xl:col-span-2"
        >
          <ColumnChart
            buckets={
              hours &&
              hourBuckets(
                hours.map((row) => ({ key: row.key, orders: row.orders, amount: row.net })),
                report?.outlets[0]?.businessDayCutoff ?? "04:00",
                hh,
                (hour) => rowLabel(t, "hour", { key: String(hour) } as ReportRow),
              )
            }
            money={format.money}
            locale={intl}
            text={{
              peak: t("reports.chart.peakHour"),
              empty,
              caption: t("reports.chart.hour"),
              when: t("reports.dim.hour"),
              amount: t("reports.col.net"),
              orders: t("reports.col.orders"),
              ordersValue: (count) => t("reports.kpi.ordersValue", { count }),
            }}
          />
        </PanelCard>
        <PanelCard icon={<CreditCardIcon />} title={t("reports.payments")} className="h-auto min-w-0">
          <MixBreakdown
            slices={slices("payment", "total")}
            detail={detail}
            percent={format.percent}
            empty={empty}
          />
        </PanelCard>
        <PanelCard icon={<TrophyIcon />} title={t("reports.topProducts")} className="min-w-0">
          <RankedList
            items={rows("product")
              ?.slice(0, TOP_PRODUCTS)
              .map((row) => ({
                key: row.key,
                label: row.label,
                amount: BigInt(row.net),
                note: t("reports.sold", { count: row.quantity ?? 0 }),
              }))}
            money={format.money}
            empty={empty}
          />
        </PanelCard>
        <PanelCard icon={<LayoutGridIcon />} title={t("reports.categories")} className="min-w-0">
          <MixBreakdown slices={categoryMix} detail={detail} percent={format.percent} empty={empty} />
        </PanelCard>
        <PanelCard icon={<UtensilsIcon />} title={t("reports.orderTypes")} className="min-w-0">
          <MixBreakdown
            slices={slices("orderType", "net")}
            detail={detail}
            percent={format.percent}
            empty={empty}
          />
        </PanelCard>
      </div>
    </section>
  );
}

function Breakdowns(props: {
  report: SalesReportView | undefined;
  loading: boolean;
  error: boolean;
  refetch: () => void;
  onDrill: (drill: Drill) => void;
}) {
  const { t, format } = useReportMessages();
  const financial = props.report?.financial ?? false;
  const several = (props.report?.outlets.length ?? 0) > 1;
  const dimensions = useMemo(() => DIMENSIONS.filter((d) => d !== "outlet" || several), [several]);
  const columns = useMemo(
    () => new Map(dimensions.map((d) => [d, columnsFor(t, format, d, financial)])),
    [dimensions, t, format, financial],
  );
  return (
    <section aria-labelledby="reports-breakdowns" className="flex min-w-0 flex-col gap-3">
      <SectionHeading
        id="reports-breakdowns"
        title={t("reports.breakdowns")}
        caption={t("reports.breakdownsHint")}
      />
      <Tabs defaultValue="product">
        <div className="-mx-4 overflow-x-auto px-4 md:mx-0 md:px-0">
          {/* Seven tabs never fit a phone: keep their natural width and let the strip scroll. */}
          <TabsList className="max-sm:group-data-[orientation=horizontal]/tabs:w-max">
            {dimensions.map((d) => (
              <TabsTrigger key={d} value={d}>
                {t(`reports.dim.${d}`)}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>
        {dimensions.map((d) => (
          <TabsContent key={d} value={d} className="flex flex-col gap-3">
            <DataTable
              paging="pages"
              label={t(`reports.dim.${d}`)}
              columns={columns.get(d) ?? []}
              query={staticRows(props.report?.breakdowns[d] ?? [], props.loading, props.error, props.refetch)}
              getRowId={(row) => row.key}
              rowActions={(row) => [
                {
                  label: t("reports.viewSales"),
                  icon: <ReceiptTextIcon />,
                  onSelect: () => props.onDrill({ dimension: d, key: row.key, label: rowLabel(t, d, row) }),
                },
              ]}
              empty={{ title: t("reports.emptyTitle"), description: t("reports.emptyHint") }}
            />
            {props.report?.truncated.includes(d) && (
              <p className="text-muted-foreground text-sm">
                {t("reports.truncated", { count: MAX_REPORT_ROWS })}
              </p>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </section>
  );
}

/**
 * From the lines as priced down to what stayed after refunds: each subtotal is set apart and weighted,
 * so the sum reads as a statement.
 */
function Summary(props: { report: SalesReportView | undefined; onDrill: () => void }) {
  const { t, format } = useReportMessages();
  const s = props.report?.summary;
  const money = (value: string | undefined) => (value === undefined ? "—" : format.money(BigInt(value)));
  type Row = [label: string, value: string, strong?: boolean];
  const groups: Row[][] = [
    [
      [t("reports.summary.gross"), money(s?.gross)],
      [t("reports.summary.discounts"), money(s?.discounts)],
      [t("reports.summary.net"), money(s?.net), true],
    ],
    [
      [t("reports.summary.serviceCharge"), money(s?.serviceCharge)],
      [t("reports.summary.tax"), money(s?.tax)],
      [t("reports.summary.total"), money(s?.total), true],
    ],
    [
      [t("reports.summary.refunds"), money(s?.refunds.amount)],
      [t("reports.summary.afterRefunds"), money(s?.totalAfterRefunds), true],
    ],
    [
      [t("reports.summary.awaiting"), money(s?.awaitingPayment.amount)],
      [t("reports.summary.receivable"), money(s?.marketplaceReceivable.amount)],
    ],
    // No costed sale: the estimate has nothing to stand on, so it isn't shown as a zero.
    ...(s?.estimatedMargin
      ? [
          [
            [
              t("reports.summary.cost"),
              s.estimatedMargin.costedOrders === 0 ? "—" : money(s.estimatedMargin.cost),
            ],
            [
              t("reports.summary.margin"),
              s.estimatedMargin.costedOrders === 0 ? "—" : money(s.estimatedMargin.margin),
              true,
            ],
          ] as Row[],
        ]
      : []),
  ];
  return (
    <PanelCard icon={<ReceiptTextIcon />} title={t("reports.summary")}>
      <div className="flex flex-col gap-4">
        <dl className="flex flex-col divide-y text-sm">
          {groups.map((rows) => (
            <div key={rows[0]?.[0]} className="flex flex-col gap-2 py-3 first:pt-0">
              {rows.map(([label, value, strong]) => (
                <div key={label} className="flex items-baseline justify-between gap-4">
                  <dt className={strong ? "font-medium" : "text-muted-foreground"}>{label}</dt>
                  <dd className={cn("text-right tabular-nums", strong && "font-semibold text-base")}>
                    {value}
                  </dd>
                </div>
              ))}
            </div>
          ))}
        </dl>
        <Button type="button" variant="outline" onClick={props.onDrill} disabled={!s || s.orders === 0}>
          <ReceiptTextIcon />
          {t("reports.drill.all")}
        </Button>
      </div>
    </PanelCard>
  );
}

function InclusionRules(props: { financial: boolean }) {
  const { t } = useReportMessages();
  const rules = [
    t("reports.rules.sales"),
    t("reports.rules.refunds"),
    t("reports.rules.offline"),
    t("reports.rules.marketplace"),
    t("reports.rules.cash"),
    t("reports.rules.category"),
    ...(props.financial ? [t("reports.rules.margin")] : []),
  ];
  return (
    <PanelCard icon={<InfoIcon />} title={t("reports.rules")}>
      <ul className="flex flex-col gap-3 text-muted-foreground text-sm">
        {rules.map((rule) => (
          <li key={rule} className="flex gap-2.5">
            <CircleCheckIcon aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-success" />
            <span>{rule}</span>
          </li>
        ))}
      </ul>
    </PanelCard>
  );
}

/** The sales behind one figure (AC-31): paged on request, newest first. */
function DrillDialog(props: {
  filters: Filters;
  drill: Drill;
  /** Each outlet's timezone: a sale's time reads on its outlet's clock, like its business day. */
  zones: ReadonlyMap<string, string>;
  onClose: () => void;
}) {
  const { t, format } = useReportMessages();
  const sales = useInfiniteQuery(drillQuery(props.filters, props.drill));
  const items = sales.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <ResponsiveDialog
      open
      onOpenChange={(open) => !open && props.onClose()}
      title={t("reports.drill.title", { label: props.drill.label })}
      description={t("reports.drill.description")}
      cancelLabel={t("reports.drill.close")}
      className="md:max-w-2xl"
    >
      {sales.isPending ? (
        <div className="flex flex-col gap-2" aria-busy="true">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      ) : sales.isError && items.length === 0 ? (
        <div className="flex flex-col items-start gap-3">
          <p role="alert">{t("reports.loadFailed")}</p>
          <Button type="button" variant="outline" onClick={() => void sales.refetch()}>
            {t("reports.retry")}
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <ul aria-label={t("reports.drill.list")} className="flex flex-col divide-y rounded-xl border">
            {items.map((sale) => (
              <li key={sale.orderId} className="flex items-center gap-3 px-4 py-3">
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="font-medium">
                    {sale.receiptNumber
                      ? `${t("reports.drill.receipt")} ${sale.receiptNumber}`
                      : t("reports.drill.noReceipt")}
                    <span className="font-normal text-muted-foreground"> · {sale.outletName}</span>
                  </span>
                  <span className="truncate text-muted-foreground text-sm">
                    <time dateTime={isoInstant(sale.completedAt)}>
                      {format.dateTime(sale.completedAt, props.zones.get(sale.outletId))}
                    </time>
                    {" · "}
                    {sale.cashier}
                    {" · "}
                    {paymentLabel(t, sale.payment)}
                  </span>
                </div>
                <div className="flex flex-col items-end gap-0.5 text-right">
                  <span className="font-medium tabular-nums">{format.money(BigInt(sale.total))}</span>
                  {sale.refunded !== "0" && (
                    <span className="text-destructive text-xs tabular-nums">
                      {t("reports.drill.refunded", { amount: format.money(BigInt(sale.refunded)) })}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
          {sales.hasNextPage && <NearEnd onReached={() => void sales.fetchNextPage()} />}
          {sales.hasNextPage && (
            <Button
              type="button"
              variant="outline"
              onClick={() => void sales.fetchNextPage()}
              disabled={sales.isFetchingNextPage}
            >
              {t(sales.isFetchingNextPage ? "kit.loading" : "kit.loadMore")}
            </Button>
          )}
        </div>
      )}
    </ResponsiveDialog>
  );
}

/**
 * Loads the next page as the list's end nears the dialog's viewport (CODING_STANDARDS §4); the button
 * under it is the fallback. A page holds 30 sales, so the dialog's list needs no virtualisation.
 */
function NearEnd(props: { onReached: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const reached = useRef(props.onReached);
  reached.current = props.onReached;
  useEffect(() => {
    const element = ref.current;
    if (!element || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) reached.current();
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return <div ref={ref} aria-hidden="true" className="h-px" />;
}

/**
 * Fetches the file through the API's link, which re-checks the caller's access on every download
 * (R1-45): links are never presigned storage URLs.
 */
async function download(view: ReportExportView): Promise<void> {
  const res = await fetch(apiUrl(view.url), { credentials: "include" });
  if (!res.ok) throw new ApiRequestError(res.status, await res.json().catch(() => ({})));
  const link = document.createElement("a");
  link.download = view.filename;
  link.href = URL.createObjectURL(await res.blob());
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(link.href), 10_000);
}

/** CSV exports of the current filter (AC-32): the server applies the caller's scope to what they hold. */
function ExportMenu(props: { filters: Filters; disabled: boolean }) {
  const { t } = useReportMessages();
  const run = useMutation({
    mutationFn: async (dataset: ReportExportDataset) => {
      const view = unwrap(
        await api.v1.reports.sales.exports.post({
          from: props.filters.from,
          to: props.filters.to,
          dataset,
          ...(props.filters.outletId === ALL ? {} : { outletId: props.filters.outletId }),
        }),
      );
      await download(view);
      return view;
    },
    onSuccess: (view) => toast.success(t("reports.export.done", { rows: view.rowCount })),
    onError: (error) =>
      toast.error(
        t(
          error instanceof ApiRequestError && error.code === "export_expired"
            ? "reports.export.expired"
            : "reports.export.failed",
        ),
      ),
  });
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="outline" disabled={props.disabled || run.isPending}>
          <DownloadIcon />
          {t(run.isPending ? "reports.export.preparing" : "reports.export")}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {(["orders", "products"] as const).map((dataset) => (
          <DropdownMenuItem key={dataset} onSelect={() => run.mutate(dataset)}>
            {t(`reports.export.${dataset}`)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
